import { PayloadAction, createSlice } from "@reduxjs/toolkit"
import parse from "@xmpp/xml/lib/parse"
import { PURGE, REHYDRATE } from "redux-persist"
import type { AppStore } from "../reducers/store"
import posthog from "posthog-js"

export type SendoutQueue = SendoutQueueItem[]

type MessageType = "chat" | "groupchat"

export type SendoutQueueItem = {
  stanza: string
  messageId: string
  messageType: MessageType
  addedAt?: string
  targetJid: string
  failed: boolean
}

export type SetAction = {
  queue: SendoutQueue
}

export type AddAction = {
  stanza: string
  messageId: string
  messageType: MessageType
}

export type MarkSendingAction = {
  messageId: string
}

export type MarkSentAction = {
  messageIds: string[]
}

export type MarkFailedAction = {
  messageId: string
}

const initialState: SendoutQueue = []

const notifyReactNative = (queue: SendoutQueue) => {
  return
}

const sendoutQueueSlice = createSlice({
  name: "sendoutQueue",
  initialState,
  reducers: {
    setQueue: (state: SendoutQueue, action: PayloadAction<SetAction>) => {
      state.splice(0, state.length, ...action.payload.queue)
    },
    add: (state: SendoutQueue, action: PayloadAction<AddAction>) => {
      const element = parse(action.payload.stanza)
      const targetJid = element.attrs.to
      state.push({
        failed: false,
        addedAt: new Date().toISOString(),
        ...action.payload,
        targetJid,
      })
      notifyReactNative(state)
    },
    markSending: (
      state: SendoutQueue,
      action: PayloadAction<MarkSendingAction>,
    ) => {
      const item = state.find(
        (item) => item.messageId === action.payload.messageId,
      )
      if (item) {
        item.failed = false
        notifyReactNative(state)
      }
    },
    markRetrying: (
      state: SendoutQueue,
      action: PayloadAction<MarkSendingAction>,
    ) => {
      const item = state.find(
        (item) => item.messageId === action.payload.messageId,
      )
      if (item) {
        item.addedAt = new Date().toISOString()
        item.failed = false
        notifyReactNative(state)
      }
    },
    markSent: (state, action: PayloadAction<MarkSentAction>) => {
      const newlySentIds = new Set(action.payload.messageIds)
      const newState = []
      for (const item of state) {
        if (!newlySentIds.has(item.messageId)) {
          newState.push(item)
        }
      }
      state.splice(0, state.length, ...newState)
      notifyReactNative(state)
    },
    markFailed: (state, action: PayloadAction<MarkFailedAction>) => {
      const item = state.find((e) => e.messageId === action.payload.messageId)

      if (item) {
        item.failed = true
        notifyReactNative(state)
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(REHYDRATE, (state, action) => {
      // @ts-ignore
      const sendoutQueue: SendoutQueue = action?.payload?.sendoutQueue
      if (sendoutQueue) {
        for (const e of sendoutQueue) {
          state.push(e)
          if (!e.failed && e.messageType === "groupchat") {
            console.info(`[XEP-0198] Retrying from store ${e.messageId}`)
            setTimeout(() => {
              window.sendoutQueueWorker.send(e.messageId)
            }, 10000)
          }
        }
        notifyReactNative(state)
      }
    })
    builder.addCase(PURGE, () => {
      notifyReactNative(initialState)
      return initialState
    })
  },
})

export const {
  setQueue,
  add,
  markSent,
  markFailed,
  markSending,
  markRetrying,
} = sendoutQueueSlice.actions

export default sendoutQueueSlice.reducer
